import { sameJson } from './json-change';

export interface WorkerTreePatch { set: { path: string[]; value: unknown }[]; unset: string[][] }
const branch = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const own = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);

/** Retained worker inputs/results share unchanged branches; only changed sections travel. */
export function workerTreePatch(previous: unknown, value: unknown): WorkerTreePatch {
    const out: WorkerTreePatch = { set: [], unset: [] };
    function visit(before: unknown, next: unknown, path: string[]): void {
        if (Object.is(before, next)) return;
        if (!branch(before) || !branch(next) || Array.isArray(before) !== Array.isArray(next)) {
            out.set.push({ path, value: next }); return;
        }
        const keys = Object.keys(next), retained = Object.keys(before).filter(key => own(next, key));
        retained.push(...keys.filter(key => !own(before, key)));
        if (keys.some((key, index) => retained[index] !== key)) { out.set.push({ path, value: next }); return; }
        for (const key of Object.keys(before)) if (!own(next, key)) out.unset.push([...path, key]);
        for (const key of keys) {
            if (!own(before, key)) out.set.push({ path: [...path, key], value: next[key] });
            else visit(before[key], next[key], [...path, key]);
        }
        if (Array.isArray(next) && (before as unknown as unknown[]).length !== next.length) out.set.push({ path: [...path, 'length'], value: next.length });
    }
    visit(previous, value, []);
    return out;
}

/** Copy changed ancestors once; unchanged facets retain their original identity. */
export function applyWorkerTreePatch(previous: unknown, patch: WorkerTreePatch): unknown {
    let result = previous;
    const copies = new WeakSet<object>();
    function write(node: unknown, path: readonly string[], value: unknown, remove: boolean): unknown {
        if (!path.length) return sameJson(node, value) ? node : value;
        if (!branch(node)) throw new Error('compile-worker-input-protocol');
        const [key, ...rest] = path;
        if (remove && !rest.length && !own(node, key!)) return node;
        const child = rest.length ? write(node[key!], rest, value, remove) : value;
        if (!remove && own(node, key!) && Object.is(child, node[key!])) return node;
        const copy = copies.has(node) ? node : Array.isArray(node) ? node.slice() as unknown as Record<string, unknown> : { ...node };
        copies.add(copy);
        if (remove && !rest.length) delete copy[key!];
        else if (Array.isArray(copy) && key === 'length') copy.length = Number(child);
        else Object.defineProperty(copy, key!, { value: child, enumerable: true, writable: true, configurable: true });
        return copy;
    }
    for (const path of patch.unset) result = write(result, path, undefined, true);
    for (const item of patch.set) result = write(result, item.path, item.value, false);
    return result;
}
